import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType, createEffect } from '@ngrx/effects';

import { merge, of } from 'rxjs';
import { map, catchError, switchMap, mergeMap, tap, exhaustMap } from 'rxjs/operators';

import * as auditActions from '../actions/audit.action';

import { CommonService } from '../../services/audit.service';
import { AuditEventService } from './../../services/event.service';

@Injectable()
export class AuditEffects {
  constructor(
    private actions$: Actions,
    private commonService: CommonService,
    private eventService: AuditEventService,
    private router: Router
  ) {}

  requestAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.loadNextAudit),
      exhaustMap((res) => {
        const state = sessionStorage.getItem('state');
        if (state === null || state === '') {
          const nextAudit$ = this.commonService.loadNextAudit(res.authorsList,
            res.company,
            res.product,
            res.date_in,
            res.date_out,
            res.analisis);
          const dataAudit$ = this.commonService.getCurrentAudit();

          return nextAudit$.pipe(
              map((res) =>
                auditActions.loadNextAuditSuccess({
                  payload: res,
                  loaded: true,
                })
              ),
              catchError((err) => of(auditActions.loadNextAuditFail(err)))
            );
        }
        return this.commonService.getCurrentAudit().pipe(
          map((res) => {
            return auditActions.loadNextAuditSuccess({ payload: res, loaded: false })
          }
          ),
          catchError((err) => of(auditActions.loadNextAuditFail(err)))
        );
      })
    )
  );

  loadNextFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(auditActions.loadNextAuditFail),
        tap(() => {
          this.router.navigateByUrl('/error');
        })
      ),
    { dispatch: false }
  );

  reloadAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.reloadAudit),
      mergeMap(() => {
        return this.commonService.getCurrentAudit().pipe(
          map((res) => auditActions.successReloadAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );

  selectDoc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.selectDoc),
      switchMap((action) => {
        return this.eventService.selectDocument(action.key, action.button).pipe(
          map((res) => auditActions.successPostAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );

  allowField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.allowFieldChange),
      mergeMap((action) => {
        return this.eventService.allowFieldChange(action.target).pipe(
          map((res) => auditActions.successPostAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );

  changeField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.changeFieldValue),
      mergeMap((action) => {
        return this.eventService
          .changeFieldValue(action.target, action.value)
          .pipe(
            map((res) => auditActions.successPostAction({ payload: res })),
            catchError((err) => of(auditActions.errorPostAction(err)))
          );
      })
    )
  );

  approveField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.approveField),
      mergeMap((action) => {
        return this.eventService.approveField(action.target).pipe(
          map((res) => auditActions.successPostAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );

  reproveField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.reproveField),
      mergeMap((action) => {
        return this.eventService.reproveField(action.target).pipe(
          map((res) => auditActions.successPostAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );

  selectImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.selectImage),
      mergeMap((action) => {
        return this.eventService.selectImage(action.key, action.button).pipe(
          map((res) => auditActions.successPostAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );
  reproveImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.reproveImage),
      mergeMap((action) => {
        return this.eventService
          .reproveImage(action.target, action?.reasons, action.note)
          .pipe(
            map((res) => auditActions.successPostAction({ payload: res })),
            catchError((err) => of(auditActions.errorPostAction(err)))
          );
      })
    )
  );

  approveImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.approveImage),
      mergeMap((action) => {
        return this.eventService.approveImage(action.target).pipe(
          map((res) => auditActions.successPostAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );
  timeoutAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.timeOutAction),
      mergeMap(() => {
        return this.eventService.timeoutAudit().pipe(
          map((res) => auditActions.successPostAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );

  finishAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.finishAuditAction),
      mergeMap(() => {
        return this.eventService.finishAudit().pipe(
          map((res) => auditActions.successPostAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );

  makeStatistics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(auditActions.makeStatisticsAction),
      mergeMap(() => {
        return this.commonService.makeStatistics().pipe(
          map((res) => auditActions.successPostAction({ payload: res })),
          catchError((err) => of(auditActions.errorPostAction(err)))
        );
      })
    )
  );
}
